import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { WebsiteNote } from '@/types/website';
import { WEBSITE_LABELS } from '@/types/website';
import { cn } from '@/lib/utils';

interface NoteCardProps {
  note: WebsiteNote;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

const websiteColors: Record<string, string> = {
  greenvision: 'border-l-green-500',
  gv_charging: 'border-l-blue-500',
};

export function NoteCard({ note, onEdit, onDelete, canEdit }: NoteCardProps) {
  const creator = note.created_profile;
  const initials = creator?.display_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  return (
    <Card className={cn('border-l-4', websiteColors[note.website])}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">
                  {creator?.display_name || creator?.email || 'Onbekend'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(note.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{note.note}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <Badge variant="outline" className="text-xs">
                  {WEBSITE_LABELS[note.website]}
                </Badge>
                {note.labels?.map((label) => (
                  <Badge key={label} variant="secondary" className="text-xs">
                    {label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
